/**
 * Admin Pristine Quote Pipeline + Quote Builder — /admin/quotes
 *
 * Single page: master/detail layout.
 *   LEFT — pipeline list (status pills + filters)
 *   RIGHT — selected quote detail with:
 *           • customer + property + test snapshot
 *           • triggered recommendations
 *           • LINE-ITEM BUILDER (toggle inclusion, Twin/Triple variant swap,
 *             qty edit, dynamic subtotal/total)
 *           • valid-until date picker
 *           • status pipeline buttons
 *           • Download PDF
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, FileText, Sparkles, AlertCircle } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatDateAU, formatDateTimeAU } from "@/lib/formatDateAU";

const STATUS_PILL = {
  new:         { l: "New",          bg: "bg-cyan-100",    fg: "text-cyan-800" },
  in_progress: { l: "In progress",  bg: "bg-amber-100",   fg: "text-amber-800" },
  quote_sent:  { l: "Quote sent",   bg: "bg-violet-100",  fg: "text-violet-800" },
  accepted:    { l: "Accepted",     bg: "bg-emerald-100", fg: "text-emerald-800" },
  declined:    { l: "Declined",     bg: "bg-rose-100",    fg: "text-rose-800" },
  follow_up:   { l: "Follow-up",    bg: "bg-slate-200",   fg: "text-slate-800" },
};
const NEXT_STATUSES = ["new", "in_progress", "quote_sent", "accepted", "declined", "follow_up"];


export default function AdminQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (preselectId = null) => {
    setLoading(true);
    try {
      const url = filter ? `/admin/pristine/quote-requests?status=${filter}` : "/admin/pristine/quote-requests";
      const { data } = await api.get(url);
      setQuotes(data || []);
      if (preselectId) setSelected((data || []).find((q) => q.id === preselectId) || null);
      else if (!selected && data?.[0]) setSelected(data[0]);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load quotes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <PortalShell title="Pristine Quotes" subtitle="Convert recommendations into tailored Pristine Water Systems estimates.">
      <div className="flex items-center gap-2 mb-4">
        <select
          value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="quotes-filter"
          className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white"
        >
          <option value="">All statuses</option>
          {NEXT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_PILL[s].l}</option>)}
        </select>
        <Button onClick={() => load()} variant="outline" size="sm" className="rounded-full" data-testid="quotes-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        <div className="ml-auto text-xs text-slate-500">{quotes.length} quote{quotes.length === 1 ? "" : "s"}</div>
      </div>

      <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-4">
        {/* List */}
        <aside className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-5 text-sm text-slate-500">Loading…</div>
          ) : quotes.length === 0 ? (
            <div className="p-5 text-sm text-slate-500">No quotes yet. Once customers submit requests they'll appear here.</div>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[calc(100vh-220px)] overflow-y-auto">
              {quotes.map((q) => {
                const isSel = selected?.id === q.id;
                const pill = STATUS_PILL[q.status] || STATUS_PILL.new;
                return (
                  <li key={q.id}>
                    <button onClick={() => setSelected(q)}
                      data-testid={`quote-row-${q.id}`}
                      className={`w-full text-left px-4 py-3 transition-colors ${isSel ? "bg-cyan-50/70" : "hover:bg-slate-50"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900 text-sm truncate">{q.customer_snapshot?.name || "—"}</div>
                        <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${pill.bg} ${pill.fg}`}>{pill.l}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">{q.property_snapshot?.address}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">${(q.totals?.total || 0).toFixed(2)} · {formatDateAU(q.created_at)}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* Detail */}
        <main>
          {selected ? (
            <QuoteDetail key={selected.id} quote={selected} onChanged={() => load(selected.id)} busy={busy} setBusy={setBusy} />
          ) : (
            <div className="rounded-3xl bg-white border border-slate-200 p-8 text-center text-sm text-slate-500">
              Select a quote on the left to view and edit details.
            </div>
          )}
        </main>
      </div>
    </PortalShell>
  );
}


function QuoteDetail({ quote, onChanged, busy, setBusy }) {
  const [items, setItems] = useState(quote.line_items || []);
  const [validUntil, setValidUntil] = useState(quote.valid_until || "");
  const [notes, setNotes] = useState(quote.admin_notes || "");

  useEffect(() => {
    setItems(quote.line_items || []);
    setValidUntil(quote.valid_until || "");
    setNotes(quote.admin_notes || "");
  }, [quote]);

  const totals = useMemo(() => {
    const included = items.filter((i) => i.included);
    const subtotal = included.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.qty) || 1), 0);
    return { included: included.length, subtotal };
  }, [items]);

  const setIncluded = (key, included) => {
    setItems((arr) => {
      const t = arr.find((x) => x.key === key);
      if (!t) return arr;
      const next = arr.map((x) => x.key === key ? { ...x, included } : x);
      // Enforce single-variant when enabling — uncheck siblings of the same variant_group
      if (included && t.variant_group) {
        return next.map((x) => (x.variant_group === t.variant_group && x.key !== key) ? { ...x, included: false } : x);
      }
      return next;
    });
  };
  const setQty = (key, qty) => setItems((arr) => arr.map((x) => x.key === key ? { ...x, qty: Math.max(1, parseInt(qty || 1)) } : x));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/pristine/quote-requests/${quote.id}/line-items`, {
        line_items: items, valid_until: validUntil || null, admin_notes: notes || null,
      });
      toast.success("Quote saved");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.put(`/admin/pristine/quote-requests/${quote.id}/status`, { status });
      toast.success(`Status → ${STATUS_PILL[status].l}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Status change failed"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/pristine/quote-requests/${quote.id}/quote.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pristine-quote-${quote.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download PDF"); }
  };

  const cust = quote.customer_snapshot || {};
  const prop = quote.property_snapshot || {};
  const test = quote.test_snapshot || {};
  const recs = quote.recommendations_snapshot || [];
  const variantGroups = useMemo(() => {
    const groups = {};
    items.forEach((i) => { if (i.variant_group) (groups[i.variant_group] ||= []).push(i); });
    return groups;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="quote-detail">
      {/* Header */}
      <div className="rounded-3xl bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Quote ref</div>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5">{quote.id.slice(0, 8).toUpperCase()}</h2>
            <div className="text-xs text-slate-500 mt-1">Submitted {formatDateTimeAU(quote.created_at)}</div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {NEXT_STATUSES.map((s) => {
              const pill = STATUS_PILL[s];
              const active = quote.status === s;
              return (
                <button key={s} onClick={() => !active && setStatus(s)} disabled={busy}
                  data-testid={`quote-status-${s}`}
                  className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${
                    active ? `${pill.bg} ${pill.fg} ring-2 ring-offset-1 ring-slate-300` : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}>{pill.l}</button>
              );
            })}
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
          <Block label="Customer" value={<>{cust.name}<br/><span className="text-xs text-slate-500">{cust.email} · {cust.phone}</span></>} />
          <Block label="Property" value={<>{prop.address}<br/><span className="text-xs text-slate-500">{(prop.property_type || "").toString()}</span></>} />
          <Block label="Water test" value={<>{test?.test_name || "—"}<br/><span className="text-xs text-slate-500">Health Score {test?.health_score ?? "—"} · {test?.band || "—"}</span></>} />
        </div>
        {cust.notes && <p className="mt-3 text-xs text-slate-600 bg-slate-50 rounded-lg p-3 border border-slate-100"><b>Customer note:</b> {cust.notes}</p>}
      </div>

      {/* Recommendations summary */}
      {recs.length > 0 && (
        <div className="rounded-3xl bg-white border border-slate-200 p-5">
          <h3 className="font-display text-base font-bold text-slate-900 inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-cyan-600" /> Triggered recommendations</h3>
          <ul className="mt-3 space-y-1.5 text-sm">
            {recs.map((r) => (
              <li key={r.id || r.key} className="flex items-start gap-2">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                  r.priority === "critical" ? "bg-rose-100 text-rose-700" :
                  r.priority === "high" ? "bg-amber-100 text-amber-700" :
                  "bg-slate-100 text-slate-600"
                }`}>{r.priority || "—"}</span>
                <span className="text-slate-700">{r.title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Line-item builder */}
      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden" data-testid="quote-builder">
        <header className="px-5 py-4 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
          <h3 className="font-display text-base font-bold text-slate-900">Quote builder</h3>
          <div className="text-xs text-slate-500 inline-flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> All items optional — toggle to include in this quote.</div>
        </header>
        <ul className="divide-y divide-slate-100">
          {items.map((li) => (
            <li key={li.key} className="px-5 py-3 flex items-center gap-4 flex-wrap" data-testid={`quote-line-${li.key}`}>
              <label className="flex items-start gap-2 flex-1 min-w-[260px] cursor-pointer">
                <input type="checkbox" checked={!!li.included}
                  onChange={(e) => setIncluded(li.key, e.target.checked)}
                  data-testid={`quote-line-toggle-${li.key}`}
                  className="mt-0.5 rounded text-cyan-600" />
                <div>
                  <div className="font-semibold text-slate-900 text-sm">
                    {li.name}
                    {li.variant_label && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-cyan-700 bg-cyan-100 rounded px-1.5 py-0.5">{li.variant_label}</span>}
                  </div>
                  <div className="text-xs text-slate-500">{li.description}</div>
                </div>
              </label>
              <div className="flex items-center gap-3">
                <input type="number" min={1} max={99} value={li.qty}
                  onChange={(e) => setQty(li.key, e.target.value)}
                  data-testid={`quote-line-qty-${li.key}`}
                  className="w-14 h-9 rounded-lg border border-slate-200 px-2 text-sm text-right" />
                <div className="text-sm tabular-nums text-slate-900 w-24 text-right font-semibold">${(li.price * li.qty).toFixed(2)}</div>
              </div>
            </li>
          ))}
        </ul>
        <footer className="px-5 py-4 border-t border-slate-100 bg-slate-50/60 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <label className="inline-flex items-center gap-2">
              <span className="text-slate-600 text-xs font-semibold">Valid until</span>
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                data-testid="quote-valid-until"
                className="h-9 rounded-lg border border-slate-200 px-2 text-sm" />
            </label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="quote-admin-notes"
              placeholder="Admin notes (optional)…"
              className="h-9 rounded-lg border border-slate-200 px-3 text-sm min-w-[200px]" />
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Quote total</div>
              <div className="font-display text-2xl font-bold text-slate-900 tabular-nums" data-testid="quote-total">${totals.subtotal.toFixed(2)} <span className="text-xs font-medium text-slate-500">AUD</span></div>
              <div className="text-[11px] text-slate-500">{totals.included} item{totals.included === 1 ? "" : "s"} included</div>
            </div>
            <Button onClick={save} disabled={busy} data-testid="quote-save-btn" className="rounded-full bg-slate-900 hover:bg-cyan-700 text-white">
              {busy ? "Saving…" : "Save changes"}
            </Button>
            <Button onClick={downloadPdf} variant="outline" data-testid="quote-download-pdf" className="rounded-full">
              <Download className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}


function Block({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
